import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useEntitlements } from '../hooks/useEntitlements';
import { useAuth } from '../AuthContext';
import { useToast } from '../contexts/ToastContext';
import { LoadingButtonContent } from './LoadingComponents';
import { createNetworkError, createAuthError, createCheckoutError, createTokenError, parseErrorMessage, createRetryableAction } from '../utils/errorHandling';

interface ProUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CheckoutRequest {
  priceId: string;
  productId: 'pro';
  mode: 'payment';
}

interface CheckoutResponse {
  url: string;
}

const ProUpgradeModal: React.FC<ProUpgradeModalProps> = ({ isOpen, onClose }) => {
  const { user, hasPro, hasPlus } = useEntitlements();
  const { signInWithGoogle } = useAuth();
  const { showError, showSuccess, showInfo } = useToast();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isUpgrade = hasPlus; // User is upgrading from Plus to Pro
  const priceId = isUpgrade
    ? 'price_1SYS14CR1GWecDhIpueAApV7' // Upgrade price (300 CZK)
    : 'price_1S81xhCR1GWecDhI7CNL2ELF'; // Full Pro price (700 CZK)

  const priceDisplay = isUpgrade ? 'CZK 300' : 'CZK 700';

  // Create retryable checkout action
  const performCheckout = createRetryableAction(async (idToken: string) => {
    const checkoutRequest: CheckoutRequest = {
      priceId: priceId,
      productId: 'pro',
      mode: 'payment'
    };

    const response = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(checkoutRequest)
    });

    if (!response.ok) {
      if (response.status >= 500) {
        throw createNetworkError('Server je dočasně nedostupný. Zkus to za chvilku.');
      } else if (response.status === 401) {
        throw createTokenError('Přihlas se prosím znovu.');
      } else {
        throw createCheckoutError(`Checkout selhal (${response.status}). Zkus to znovu.`);
      }
    }

    return response.json();
  }, 2, 1500);

  // Handle checkout flow
  const handleCheckout = async () => {
    if (!user) {
      try {
        showInfo('Přihlašování...', 'Přesměrováváme tě na přihlášení přes Google.');
        await signInWithGoogle();
        showSuccess('Přihlášení úspěšné!', 'Nyní můžeš pokračovat k nákupu.');
        return;
      } catch (error) {
        console.error('Sign-in failed:', error);
        const errorInfo = parseErrorMessage(createAuthError('Přihlášení se nezdařilo. Zkus to prosím znovu.'));
        setError(errorInfo.message);
        showError(errorInfo.title, errorInfo.message);
        return;
      }
    }

    setCheckoutLoading(true);
    setError(null);

    try {
      showInfo('Připravujeme checkout...', 'Chvilku strpení, otevíráme platební bránu.');

      const idToken = await user.getIdToken();
      const data: CheckoutResponse = await performCheckout(idToken);

      showSuccess('Přesměrování na platbu', 'Otevíráme Stripe checkout...');

      setTimeout(() => {
        window.location.href = data.url;
      }, 1000);

    } catch (error) {
      console.error('Checkout error:', error);
      const errorInfo = parseErrorMessage(error);
      setError(errorInfo.message);
      showError(errorInfo.title, errorInfo.message);
      setCheckoutLoading(false);
    }
  };

  // Handle sign-in for signed-out users
  const handleSignIn = async () => {
    try {
      showInfo('Přihlašování...', 'Přesměrováváme tě na přihlášení přes Google.');
      await signInWithGoogle();
      showSuccess('Přihlášení úspěšné!', 'Vítej zpět! Nyní máš přístup ke svému účtu.');
    } catch (error) {
      console.error('Sign-in failed:', error);
      const errorInfo = parseErrorMessage(createAuthError('Přihlášení se nezdařilo. Zkus to prosím znovu.'));
      setError(errorInfo.message);
      showError(errorInfo.title, errorInfo.message);
    }
  };

  // Safety check: never show modal for Pro users or when not open
  if (!isOpen || hasPro) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          aria-label="Zavřít"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="grid md:grid-cols-2 min-h-[500px]">
          {/* Left side - Visual with gradient */}
          <div className="relative bg-gradient-to-br from-purple-500 via-blue-500 to-pink-500 p-8 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                <span className="text-4xl">🚀</span>
              </div>
              <h2 className="text-3xl font-bold mb-2">Pro Access</h2>
              <p className="text-lg opacity-90">
                Odemkni plný potenciál svého studia
              </p>
            </div>
          </div>

          {/* Right side - Content */}
          <div className="p-8 flex flex-col justify-center">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Vítej! 👋
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Vidím, že už jsi prošel onboardingem. Chceš si odemknout všechny funkce a urychlit své studium?
              </p>

              {/* Benefits list */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center mr-3">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Všechny kapitoly a testy
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center mr-3">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Flashcards + opakování chyb
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center mr-3">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Audioknihy (offline poslech později)
                  </span>
                </div>
              </div>

              {/* Pricing */}
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-gray-900 dark:text-white">
                  {priceDisplay}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Jednorázová platba • Doživotní přístup
                </div>
              </div>
            </div>

            {/* Error messages */}
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Action buttons */}
            <div className="space-y-3">
              {user ? (
                <button
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  className="w-full rounded-xl px-4 py-3 font-medium font-poppins bg-brand-orange hover:bg-brand-orange/90 disabled:opacity-60 text-white transition-colors duration-200 flex items-center justify-center"
                >
                  <LoadingButtonContent
                    isLoading={checkoutLoading}
                    loadingText="Otevírám Checkout…"
                    defaultText="🚀 Upgrade na Pro"
                  />
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCheckout}
                    disabled={checkoutLoading}
                    className="w-full rounded-xl px-4 py-3 font-medium font-poppins bg-brand-orange hover:bg-brand-orange/90 disabled:opacity-60 text-white transition-colors duration-200 flex items-center justify-center"
                  >
                    <LoadingButtonContent
                      isLoading={checkoutLoading}
                      loadingText="Otevírám Checkout…"
                      defaultText="🚀 Upgrade na Pro"
                    />
                  </button>
                  <button
                    onClick={handleSignIn}
                    className="w-full rounded-xl px-4 py-3 font-medium bg-white/10 hover:bg-white/20 text-gray-700 dark:text-gray-300 transition-colors border border-gray-200 dark:border-gray-600"
                  >
                    Přihlásit přes Google
                  </button>
                </>
              )}

              <button
                onClick={onClose}
                className="w-full rounded-xl px-4 py-3 font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                Možná později
              </button>
            </div>

            {/* Trust indicators */}
            <div className="mt-4 text-center text-xs text-gray-400">
              🔒 Platba přes Stripe • Bezpečné
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProUpgradeModal;